import React, { useRef } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Grid } from '@mui/material';
import EditorActions from '@/components/EditorActions.tsx';
import { ExpandMore, ViewList } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@core/hooks/useNotifications';
import { FormContainer } from '@/components';
import { FormContainerRef } from '@/components/form/FormContainer.tsx';
import FormTextField from '@/components/form/FormTextField.tsx';
import { required } from '@core/utils/validation.ts';
import FormSelect from '@/components/form/FormSelect.tsx';
import { ProductType } from '@core/store/slices/apiSlice.ts';

interface ProductEditorProps {
    item: any;
    itemTypes?: ProductType[];
    onSave: (item: any) => void;
    onCancel: () => void;
}

const RfidTagProductEditor: React.FC<ProductEditorProps> = ({ item, itemTypes, onSave, onCancel }) => {
    const { t } = useTranslation();
    const { notifyWarning } = useNotifications();
    const [name, setName] = React.useState(item?.name || '');
    const [articleNumber, setArticleNumber] = React.useState(item?.articleNumber || '');
    const [productTypeId, setProductTypeId] = React.useState(item?.productTypeId || '');
    const [formValid, setFormValid] = React.useState(true);
    const formRef = useRef<FormContainerRef>(null);

    const handleSave = () => {
        const isValid = formRef.current?.validate?.() ?? formValid;
        if (!isValid) {
            notifyWarning(formRef.current?.getRequiredWarning() || 'required');
            return;
        }

        setFormValid(true);
        onSave({ ...item, name, articleNumber, productTypeId });
    };

    return (
        <Box className="editor-container">
            <Box className="editor-sidebar-actions">
                <EditorActions onSave={handleSave} onCancel={onCancel} saveDisabled={!formValid} />
            </Box>
            <Box className="editor-sidebar-content">
                <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">
                                <ViewList />
                                {t('product')}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={4}>
                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'name'}
                                        label={t('name')}
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        fullWidth
                                        rules={[required(t)]}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'articleNumber'}
                                        label={t('articleNumber')}
                                        value={articleNumber}
                                        onChange={(e) => setArticleNumber(e.target.value)}
                                        required
                                        fullWidth
                                        rules={[required(t)]}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormSelect
                                        name={'productTypeId'}
                                        label={t('productType')}
                                        value={productTypeId}
                                        items={(itemTypes || []).map((type) => ({
                                            id: type.id,
                                            name: type.typeName || type.id
                                        }))}
                                        itemText={'name'}
                                        placeholder={t('selectAType')}
                                        itemValue={'id'}
                                        onChange={(e) => setProductTypeId(e)}
                                        rules={[required(t)]}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </FormContainer>
            </Box>
        </Box>
    );
};

export default RfidTagProductEditor;
